<template>
    <page :busy="false">
        <b-container fluid class="mb-2">
            <b-row>
                <b-col cols="12" md="6">
                    <h4 class="title-header">Campaign Details</h4>
                    <div class="notification-ui">
                        <img src="/static/images/staylio_app_icon.png" alt="Staylio Icon" class="img-fluid" />
                        <div class="notification-content">
                            <div class="notification-header">
                                <h3>{{ campaignDetails.title }}</h3>
                                <small>{{ formatDate(campaignDetails.created) }}</small>
                            </div>
                            <div v-if="campaignDetails.message" style="overflow: auto">
                                <p>
                                    {{
                                        expandText
                                            ? campaignDetails.message
                                            : campaignDetails.message.length > characterPreviewLimit
                                            ? campaignDetails.message.substring(0, characterPreviewLimit) + '...'
                                            : campaignDetails.message.substring(0, characterPreviewLimit)
                                    }}
                                </p>
                            </div>
                            <div class="notification-expand-button" v-if="campaignDetails.message">
                                <b-button
                                    v-if="campaignDetails.message.length > characterPreviewLimit"
                                    @click="expandText = !expandText"
                                >
                                    {{ expandText ? 'Show Less' : 'Show More' }}
                                </b-button>
                            </div>
                        </div>
                    </div>
                </b-col>
                <b-col cols="12" md="6" style="min-height: 350px" v-if="campaignDetails.status == 'Sent'">
                    <h4 class="title-header">Campaign Performance</h4>
                    <vue-apex-charts type="bar" :options="options" :series="series" height="100%" width="100%" />
                </b-col>

                <b-col cols="12" md="6" style="min-height: 350px" v-else>
                    <h4 class="title-header">Campaign Performance</h4>
                    <b-alert :variant="alertVariant" show>
                        <h4 class="alert-heading">Campaign {{ campaignDetails.status }}</h4>
                        <p>
                            {{ campaignAlertMessage }}
                        </p>
                        <b-button
                            variant="primary"
                            @click="retrySend"
                            class="mt-2 ml-2"
                            style="min-width: 150px"
                            v-if="campaignDetails.status == 'Failed'"
                        >
                            Retry
                            <font-awesome-icon :icon="['fas', 'paper-plane']" class="ml-2"></font-awesome-icon>
                        </b-button>
                    </b-alert>
                </b-col>
            </b-row>
        </b-container>
        <grid
            v-if="campaignDetails.status == 'Sent'"
            style="min-height: 350px"
            ref="grid"
            :column-defs="columnDefs"
            :fetch-callback="fetchCallback"
            :is-auto-width="true"
            :getRowStyle="rowStyle"
            :isSearch="true"
            :buttonToolbarClass="['align-items-start flex-md-nowrap']"
            :defaultSort="sort"
            :defaultDesc="true"
        ></grid>
    </page>
</template>

<script>
import { mapGetters } from 'vuex';
import API from '../../../api';
import { MASS_PUSH_NOTIFICATION_ENDPOINT, PUSH_NOTIFICATION_ENDPOINT } from '../../../endpoints';
import page from '../../generic/page/page.vue';
import Grid from '../../generic/grid/grid.vue';
import format from 'date-fns/format';
import parseISO from 'date-fns/parseISO';
import VueApexCharts from 'vue3-apexcharts';

export default {
    components: {
        page,
        Grid,
        VueApexCharts,
    },
    name: 'MassPushNotificationDetail',
    data() {
        return {
            busy: false,
            campaignDetails: {},
            expandText: false,
            characterPreviewLimit: 250,
            columnDefs: [
                {
                    field: 'guest.id',
                    headerName: 'ID',
                    type: 'textColumn',
                    minWidth: 100,
                },
                {
                    field: 'guest.fullname',
                    headerName: 'Guest Name',
                    type: 'textColumn',
                    minWidth: 250,
                    autoWidthExclude: true,
                },
                {
                    field: 'delivered_status',
                    headerName: 'Delivered',
                    type: 'dateTimeColumn',
                    minWidth: 125,
                    cellRenderer: this.dateTimeRenderer,
                },
                {
                    field: 'read_status',
                    headerName: 'Read',
                    type: 'dateTimeColumn',
                    minWidth: 125,
                    cellRenderer: this.dateTimeRenderer,
                },
                {
                    field: 'failed_status',
                    headerName: 'Failed',
                    type: 'dateTimeColumn',
                    minWidth: 125,
                    cellRenderer: this.dateTimeRenderer,
                },
            ],
            rowStyle: null,
            offset: 0,
            limit: 20,
            sort: 'guest.fullname',
            desc: false,
            search: '',
            data: [],
            percentageData: [],
            labels: ['Delivered', 'Read', 'Pending Delivery', 'Failed'],
        };
    },
    computed: {
        ...mapGetters('header', ['userUnicodeDateFormat', 'userTimeFormat']),
        id() {
            return this.$route.params.id;
        },
        pinnedBottomRowData() {
            return [{}];
        },
        fetchCallback() {
            return ({ offset, limit, sort, desc, search }) => {
                this.offset = offset;
                this.limit = limit;
                this.sort = sort;
                this.desc = desc;
                this.search = search;
                const mass_push_notification_campaign = this.$route.params.id;
                return API.get(`${PUSH_NOTIFICATION_ENDPOINT()}grid/`, {
                    params: {
                        offset,
                        limit,
                        sort,
                        desc,
                        search,
                        mass_push_notification_campaign,
                    },
                });
            };
        },

        options() {
            return {
                chart: {
                    width: '100%',
                    type: 'bar',
                    toolbar: {
                        show: false,
                    },
                    zoom: {
                        enabled: false,
                    },
                },
                yaxis: {
                    max: 100,
                    labels: {
                        formatter: (val) => `${val}%`,
                    },
                },
                plotOptions: {
                    bar: {
                        distributed: true,
                    },
                },
                annotations: {
                    points: this.percentageData.map((item, index) => {
                        let yPosition = item;
                        let count = this.data[index];
                        if (!(yPosition > 0)) {
                            yPosition = 0;
                        }
                        return {
                            x: this.labels[index],
                            y: yPosition,
                            marker: {
                                size: 0,
                                strokeWidth: 0,
                            },
                            label: {
                                borderColor: 'transparent',
                                style: {
                                    color: '#000',
                                    background: 'transparent',
                                },
                                text: `${count} ${count > 0 ? 'Guests' : ''}`,
                            },
                        };
                    }),
                },
                dataLabels: {
                    enabled: true,
                    formatter: (val, { dataPointIndex, w }) => {
                        const total = this.series[0].data.reduce((a, b) => a + b, 0);
                        if (val === 0 || total === 0) {
                            return ``;
                        } else {
                            return `${val}%`;
                        }
                    },
                    style: {
                        fontSize: '10px',
                    },
                },
                xaxis: {
                    labels: {
                        show: true,
                    },
                    categories: this.labels,
                },
                tooltip: {
                    custom: (params) => {
                        const { series, seriesIndex, dataPointIndex, w } = params;
                        const total = this.data[dataPointIndex];
                        const content = `${this.labels[dataPointIndex]}: ${total} Guests`;

                        return (
                            '<div class="staylio-bar-tooltip">' +
                            '<span style="margin:4px;">' +
                            content +
                            '</span>' +
                            '</div>'
                        );
                    },
                },
                legend: {
                    show: false,
                },
            };
        },
        series() {
            const total = this.campaignDetails.notification_count || 0;
            const readPercentage = (this.campaignDetails.read_count / total) * 100 || 0;
            const deliveredPercentage = (this.campaignDetails.delivered_count / total) * 100 || 0;
            const failedPercentage = (this.campaignDetails.failed_count / total) * 100 || 0;
            if (total === 0) {
                return [
                    {
                        data: [0, 0, 0, 0],
                    },
                ];
            }

            const pendingPercentage = 100 - (deliveredPercentage + failedPercentage) || 0;

            this.percentageData = [deliveredPercentage, readPercentage, pendingPercentage, failedPercentage];
            return [
                {
                    data: this.percentageData,
                },
            ];
        },

        alertVariant() {
            let variant = 'warning';
            if (this.campaignDetails.status === 'Failed') {
                variant = 'danger';
            }
            return variant;
        },

        campaignAlertMessage() {
            let message = '';
            if (this.campaignDetails.status === 'Failed') {
                message = 'The campaign failed to send. Please try again.';
            }
            if (this.campaignDetails.status === 'Pending') {
                message = 'The campaign is pending to be sent. Please wait for the campaign to be processed.';
            }
            if (this.campaignDetails.status === 'Processing') {
                message =
                    'The campaign is being processed. Please wait for the campaign to be sent to view the performance.';
            }
            return message;
        },
    },
    async mounted() {
        const response = await API.post(`${MASS_PUSH_NOTIFICATION_ENDPOINT()}get-campaign-details/`, {
            id: this.id,
        });
        this.campaignDetails = response.data;
        this.data.push(this.campaignDetails.delivered_count);
        this.data.push(this.campaignDetails.read_count);
        this.data.push(
            this.campaignDetails.notification_count -
                this.campaignDetails.delivered_count -
                this.campaignDetails.failed_count
        );
        this.data.push(this.campaignDetails.failed_count);
    },
    methods: {
        formatDate(date) {
            if (date) {
                const formattedDate = format(parseISO(date), this.userUnicodeDateFormat);
                const formattedTime = format(parseISO(date), this.userTimeFormat);
                return `${formattedDate} at ${formattedTime}`;
            }
        },
        dateTimeRenderer(params) {
            return params.value === null ? 'N/A' : this.formatDate(params.value);
        },

        async retrySend() {
            await API.post(`${MASS_PUSH_NOTIFICATION_ENDPOINT()}process-campaign/`, {
                id: this.id,
            });
            this.$router.push(`/app/staylio-app/mass-push-notification/list/`);
        },
    },
};
</script>

<style lang="scss" scoped>
.notification-ui {
    background-color: #f8f9fa;
    border: 1px solid #dee2e6;
    border-radius: 5px;
    padding: 1rem;
    margin-top: 1.5rem;
    margin-bottom: 1.75rem;
    width: 100%;
    max-height: 350px;
    display: flex;
    flex-direction: row;
    overflow: hidden;
}

.notification-ui img {
    width: 50px;
    height: 50px;
    margin: 0.5rem;
    margin-right: 1rem;
}

.notification-content h3 {
    margin: 0;
    font-size: 1.25rem;
}

.notification-content {
    display: flex;
    width: 100%;
    flex-direction: column;
}

.notification-content p {
    margin: 0.5rem 0;
    overflow: auto;
}

.notification-content small {
    display: block;
    color: #6c757d;
}

.notification-header {
    display: flex;
    justify-content: space-between;
}

.notification-expand-button {
    display: flex;
    flex-direction: row-reverse;
    margin-top: 0.5rem;
}

.staylio-bar-tooltip {
    padding: 4px;
    font-size: 10px;
    font-weight: bold;
    line-height: 1;
    color: #373d3f;
    margin: 2px;
}
.title-header {
    margin-top: 0.5rem;
}
</style>
